import { useState, useEffect, Fragment } from 'react'
import CardForm from './CardForm';

function SubscriptionForm({ priceId, planName, amount, billingPeriod = 'month', onSuccess, onError }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState('idle'); // idle, processing, succeeded, failed
    try {
        
        const handlePaymentMethodCreated = async (paymentMethodData) => {
            try {
                setPaymentMethod(paymentMethodData);
                setSubscriptionStatus('processing');
                setIsLoading(true);
                
                // Create subscription with the payment method
                const subscription = await createSubscription(priceId, paymentMethodData.id);
                
                // Subscription created successfully
                setSubscriptionStatus('succeeded');
                
                // Call onSuccess callback with the subscription
                if (onSuccess) onSuccess(subscription);
            } catch (err) {
                console.error('Subscription creation error:', err);
                setSubscriptionStatus('failed');
                setError(err.message || '創建訂閱失敗。請再試一次。');
                if (onError) onError(err);
            } finally {
                setIsLoading(false);
            }
        };
        
        const formatCurrency = (value, billingPeriod) => {
            return new Intl.NumberFormat('zh-HK', {
                style: 'currency',
                currency: 'HKD',
                minimumFractionDigits: 2
            }).format(value) + (billingPeriod ? `/${billingPeriod}` : '');
        };
        
        // Simulate creating a subscription
        const createSubscription = async (priceId, paymentMethodId) => {
            // In a real implementation, this would be a call to your backend API
            // For demo purposes, we'll simulate a successful response
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        id: 'sub_' + Math.random().toString(36).substring(2, 15),
                        status: 'active',
                        current_period_end: Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60, // 30 days from now
                        plan: {
                            id: priceId,
                            name: planName,
                            amount: amount * 100, // Convert to cents
                            interval: billingPeriod
                        },
                        payment_method: paymentMethodId
                    });
                }, 2000);
            });
        };
        
        if (subscriptionStatus === 'succeeded') {
            return (
                <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md text-center" data-name="subscription-success">
                    <div className="mb-4 text-green-500">
                        <i className="fas fa-check-circle text-5xl"></i>
                    </div>
                    <h2 className="text-2xl font-semibold mb-2">訂閱成功！</h2>
                    <p className="text-gray-600 mb-4">
                        感謝您訂閱 {planName || '我們的服務'}。
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                        您的訂閱已成功啟用。
                    </p>
                    <button
                        onClick={() => window.location.href = '/dashboard'}
                        className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        data-name="dashboard-button"
                    >
                        前往儀表板
                    </button>
                </div>
            );
        }
        
        return (
            <div className="max-w-md mx-auto" data-name="subscription-form">
                <div className="bg-white p-6 rounded-lg shadow-md mb-4">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold">訂閱 {planName || '方案'}</h2>
                        <div className="text-2xl font-bold">{formatCurrency(amount, billingPeriod)}</div>
                    </div>
                    
                    <div className="mb-6 bg-gray-50 p-4 rounded-md">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-medium">{planName || '訂閱'}</h3>
                                <p className="text-gray-600 text-sm">每{billingPeriod}計費</p>
                            </div>
                            <div className="text-lg font-medium">
                                {formatCurrency(amount, billingPeriod)}
                            </div>
                        </div>
                    </div>
                    
                    <CardForm 
                        onSubmit={handlePaymentMethodCreated}
                        isLoading={isLoading}
                        error={error}
                    />
                </div>
                
                <div className="text-center text-sm text-gray-500">
                    <p>
                        訂閱即表示您同意我們的{' '}
                        <a href="/terms" className="text-blue-600 hover:underline">服務條款</a>
                    </p>
                </div>
            </div>
        );
    } catch (error) {
        console.error('SubscriptionForm component error:', error);
        reportError(error);
        return (
            <div className="bg-red-100 p-4 rounded-md">
                <p>載入訂閱表單時出錯。請刷新頁面並重試。</p>
            </div>
        );
    }
}

export default SubscriptionForm;